import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { DetailedHTMLProps, FC, HTMLAttributes } from "react";

import { useHeaderMenu } from "../../../../hooks/use-header-menu";
import { Cta, CtaProps } from "../../../buttons/cta";
import { RichText } from "../../../rich-text";
import { HeaderMenuCards } from "../header-menu-cards";
import { HeaderMenuItemCloseButton } from "../header-menu-item-close-button";
import { linksSectionColorScheme, tabPanelColorScheme } from "./styles";
import { HeaderMenuItemTabBaseColorScheme, HeaderMenuItemTabColorScheme } from "./types";

export interface HeaderMenuItemTabProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {}

export const HeaderMenuItemTab: FC<HeaderMenuItemTabProps> = ({ className, ...rest }) => {
  const { expandedTab, expandedEditorialItem } = useHeaderMenu();

  if (!expandedTab) return null;

  const {
    title,
    body,
    logo,
    cards,
    ctas,
    colorScheme: colorSchemeAsString,
    linksSections,
  } = expandedTab;

  const isOnlyTab = !expandedEditorialItem || (expandedEditorialItem?.tabs?.length ?? 0) === 1;

  const colorScheme = (colorSchemeAsString ??
    HeaderMenuItemTabBaseColorScheme.NeutralPrimaryTitle) as HeaderMenuItemTabColorScheme;

  const linksSectionsCount = linksSections?.length ?? 0;
  const linksSectionsLayout = (() => {
    if (linksSectionsCount === 0) return undefined;
    if (linksSectionsCount === 1) return "grid-cols-1";
    if (linksSectionsCount % 2 === 0 && linksSectionsCount % 3 !== 0) return "grid-cols-2";
    return "grid-cols-3";
  })();

  return (
    <div
      className={clsx(
        "py-12 px-10 rounded-b-4xl flex flex-col gap-8 origin-top",
        "transition-all duration-300 relative w-full",
        !isOnlyTab && "-mt-10 pt-24",
        tabPanelColorScheme[colorScheme],
        className
      )}
      {...rest}
    >
      {isOnlyTab && (
        <div className="absolute right-8 top-2">
          <HeaderMenuItemCloseButton />
        </div>
      )}
      <div className="xl:container w-full !max-w-[1440px] mx-auto">
        <div className="grid grid-cols-2 grid-rows-1 gap-20 xl:gap-40 w-full">
          {/* Panel main body */}
          <div className="flex flex-col gap-4 w-full">
            {logo?.gatsbyImageData && (
              <GatsbyImage
                image={logo.gatsbyImageData}
                alt=""
                objectFit="scale-down"
                imgClassName="object-left"
                className="max-w-full max-h-10"
              />
            )}
            {/* Title and body */}
            {title && <h3 className="text-2xl font-bold">{title}</h3>}
            {body?.raw && <RichText className="text-lg mt-6" raw={body.raw} />}

            {/* Links sections */}
            {!!linksSections?.length && (
              <div
                className={clsx(
                  "flex flex-wrap xl:grid-cols-4 gap-x-24 gap-y-10",
                  linksSectionsLayout
                )}
              >
                {linksSections.map((linksSection, i) => {
                  if (!linksSection) return null;
                  const { title, ctas } = linksSection;
                  const { title: titleClass, link: linkClass } =
                    linksSectionColorScheme[colorScheme];

                  return (
                    <div key={i}>
                      {/* Links section title */}
                      <h3 className={clsx("mb-5", titleClass)}>{title}</h3>
                      {/* Links */}
                      {!!ctas?.length && (
                        <div className="flex flex-col gap-2">
                          {ctas.map(
                            (cta, i) =>
                              cta?.label && (
                                <Cta
                                  {...cta}
                                  key={i}
                                  className={clsx("hover:underline cursor-pointer", linkClass)}
                                >
                                  {cta.label}
                                </Cta>
                              )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!!cards?.length && <HeaderMenuCards className="justify-end" />}
        </div>
        {!!ctas?.length && (
          <div className="flex flex-1 gap-4 items-end mt-10">
            {ctas?.map((cta, i) => {
              if (!cta?.label) return null;
              const variant: CtaProps["variant"] =
                ctas.length > 1 && i === ctas.length - 1
                  ? "neutral-outline"
                  : colorScheme === HeaderMenuItemTabBaseColorScheme.NeutralPrimaryTitle
                  ? "primary"
                  : "primary-inverted";

              return <Cta key={i} {...cta} size="sm" variant={variant} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const query = graphql`
  fragment HeaderMenuLinksSectionData on ContentfulHeaderMenuLinksSection {
    title
    ctas {
      ...CtaData
    }
  }
  fragment HeaderMenuItemTabData on ContentfulHeaderMenuItemTab {
    id
    title
    tabLabel
    colorScheme
    logo {
      gatsbyImageData(layout: FIXED)
    }
    body {
      raw
    }
    linksSections {
      ...HeaderMenuLinksSectionData
    }
    ctas {
      ...CtaData
    }
    cards {
      ...HeaderMenuCardData
    }
  }
`;
